export const FACET_GRADES = 'grades'
export const FACET_DOMAINS = 'domains'
export const FACET_STANDARDS = 'standard_set_refs.standard_long_ref'
export const FACET_SUBJECT = 'subject'
export const FACET_CHARACTERISTICS = 'characteristics'

export const CHARACTERISTIC_ID_BLV = 'recjUHa6goKzM1jnr'
export const CHARACTERISTIC_ID_DHH = 'recw811h4t0eAEah9'

export interface BrowseFilters {
  subject?: string
  grades?: string[]
  subjectDomain?: string
  standard?: string
  keyword?: string
  levelOfSupport?: number
  characteristics?: string[] | null
}

export type BrowseFilterField = keyof BrowseFilters

export interface FacetRequest {
  filterFields: BrowseFilterField[]
  facets: string[]
}

export interface BrowseSearchPlan {
  hitsFacets: string[]
  facetRequests: FacetRequest[]
}

export interface FacetValuesWithResults {
  grades: string[] | null
  domains: string[] | null
  standards: string[] | null
}

// Which facets come back with the hits and which need their own request, keyed by
// encodeBrowseFilters. A facet a filter is applied to must be counted by a request that
// leaves that filter out, or the user could never widen it. Source:
// https://docs.google.com/spreadsheets/d/1teWchROdyD_YsVrLTqBNQg5sJaf4MVtmK3lX9jrNvK8/edit#gid=0
const SEARCH_PLANS: Record<string, BrowseSearchPlan> = {
  '10000': { hitsFacets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS], facetRequests: [] },
  '11000': { hitsFacets: [FACET_DOMAINS, FACET_STANDARDS], facetRequests: [{ filterFields: ['subject'], facets: [FACET_GRADES] }] },
  '11100': {
    hitsFacets: [FACET_STANDARDS],
    facetRequests: [
      { filterFields: ['subject', 'subjectDomain'], facets: [FACET_GRADES] },
      { filterFields: ['subject', 'grades'], facets: [FACET_DOMAINS] }
    ]
  },
  '11110': {
    hitsFacets: [],
    facetRequests: [
      { filterFields: ['subject', 'grades', 'subjectDomain'], facets: [FACET_STANDARDS] },
      { filterFields: ['subject', 'subjectDomain', 'standard'], facets: [FACET_GRADES] },
      { filterFields: ['subject', 'grades', 'standard'], facets: [FACET_DOMAINS] }
    ]
  },
  '11101': {
    hitsFacets: [FACET_STANDARDS],
    facetRequests: [
      { filterFields: ['subject', 'grades', 'keyword'], facets: [FACET_DOMAINS] },
      { filterFields: ['subject', 'subjectDomain', 'keyword'], facets: [FACET_GRADES] }
    ]
  },
  '11111': {
    hitsFacets: [],
    facetRequests: [
      { filterFields: ['subject', 'grades', 'subjectDomain', 'keyword'], facets: [FACET_STANDARDS] },
      { filterFields: ['subject', 'subjectDomain', 'standard', 'keyword'], facets: [FACET_GRADES] },
      { filterFields: ['subject', 'grades', 'standard', 'keyword'], facets: [FACET_DOMAINS] }
    ]
  },
  '11010': {
    hitsFacets: [],
    facetRequests: [
      { filterFields: ['subject'], facets: [FACET_GRADES] },
      { filterFields: ['subject', 'grades'], facets: [FACET_DOMAINS, FACET_STANDARDS] }
    ]
  },
  '11001': {
    hitsFacets: [FACET_DOMAINS, FACET_STANDARDS],
    facetRequests: [{ filterFields: ['subject', 'keyword'], facets: [FACET_GRADES] }]
  },
  '11011': {
    hitsFacets: [],
    facetRequests: [
      { filterFields: ['subject', 'grades', 'keyword'], facets: [FACET_DOMAINS, FACET_STANDARDS] },
      { filterFields: ['subject', 'keyword'], facets: [FACET_GRADES] }
    ]
  },
  '10100': { hitsFacets: [FACET_GRADES, FACET_STANDARDS], facetRequests: [{ filterFields: ['subject'], facets: [FACET_DOMAINS] }] },
  '10110': {
    hitsFacets: [],
    facetRequests: [
      { filterFields: ['subject'], facets: [FACET_DOMAINS] },
      { filterFields: ['subject', 'subjectDomain'], facets: [FACET_GRADES, FACET_STANDARDS] }
    ]
  },
  '10111': {
    hitsFacets: [],
    facetRequests: [
      { filterFields: ['subject', 'standard', 'keyword'], facets: [FACET_GRADES, FACET_DOMAINS] },
      { filterFields: ['subject', 'subjectDomain', 'keyword'], facets: [FACET_STANDARDS] }
    ]
  },
  '10101': {
    hitsFacets: [FACET_GRADES, FACET_STANDARDS],
    facetRequests: [{ filterFields: ['subject', 'keyword'], facets: [FACET_DOMAINS] }]
  },
  '10010': {
    hitsFacets: [],
    facetRequests: [{ filterFields: ['subject'], facets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS] }]
  },
  '10011': {
    hitsFacets: [],
    facetRequests: [{ filterFields: ['subject', 'keyword'], facets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS] }]
  },
  '10001': { hitsFacets: [FACET_GRADES, FACET_DOMAINS, FACET_STANDARDS], facetRequests: [] },
  '00001': { hitsFacets: [FACET_GRADES], facetRequests: [] },
  '01001': { hitsFacets: [], facetRequests: [{ filterFields: ['keyword'], facets: [FACET_GRADES] }] }
}

// One bit per filter, in the order subject, grades, subjectDomain, standard, keyword —
// e.g. subject alone is '10000'.
export function encodeBrowseFilters(filters: BrowseFilters): string {
  const presence = [
    filters.subject,
    filters.grades?.length,
    filters.subjectDomain,
    filters.standard,
    filters.keyword
  ]
  return presence.map((value) => (value ? '1' : '0')).join('')
}

export function requestsAndOptionsForBrowseFilters(filters: BrowseFilters): BrowseSearchPlan {
  const encodedFilters = encodeBrowseFilters(filters)
  const plan = SEARCH_PLANS[encodedFilters]
  if (!plan) {
    console.warn(`Unexpected browse filter combination ${encodedFilters}`)
  }
  const searchPlan = structuredClone(plan ?? { hitsFacets: [], facetRequests: [] })
  addCharacteristicsFacetRequests(searchPlan, filters)
  return searchPlan
}

// Subjects and characteristics enable and disable each other's options, so each is
// counted under the other, and grade and domain counts must respect a chosen characteristic.
function addCharacteristicsFacetRequests(searchPlan: BrowseSearchPlan, filters: BrowseFilters) {
  if (filters.characteristics?.length) {
    searchPlan.facetRequests.push({ filterFields: ['characteristics'], facets: [FACET_SUBJECT] })
    for (const request of searchPlan.facetRequests) {
      const countsGradesOrDomains = request.facets.includes(FACET_GRADES) || request.facets.includes(FACET_DOMAINS)
      if (countsGradesOrDomains && !request.filterFields.includes('characteristics')) {
        request.filterFields.push('characteristics')
      }
    }
  }
  if (filters.subject) {
    searchPlan.facetRequests.push({ filterFields: ['subject'], facets: [FACET_CHARACTERISTICS] })
  }
}

export function generateAlgoliaFacetFiltersFromBrowseFilters(filters: BrowseFilters, standardSets: string[] = []): string[][] {
  const facetFilters: string[][] = []
  const addAnyOf = (attribute: string, values: string[] | undefined | null) => {
    if (values?.length) {
      facetFilters.push(values.map((value) => `${attribute}:${value}`))
    }
  }
  const addOne = (attribute: string, value: string | undefined) => {
    if (value) {
      facetFilters.push([`${attribute}:${value}`])
    }
  }

  addOne('subject', filters.subject)
  addAnyOf('grades', filters.grades)
  addOne('domains', filters.subjectDomain)
  addOne(FACET_STANDARDS, filters.standard)
  addAnyOf('standard_sets', standardSets)
  if (filters.levelOfSupport !== undefined && filters.levelOfSupport >= 0) {
    facetFilters.push([`levels_of_support:${filters.levelOfSupport}`])
  }
  addAnyOf('characteristics', filters.characteristics)
  return facetFilters
}

// BLV and DHH goals are hidden from browsing unless their characteristic is chosen, but a
// keyword search deliberately shows everything.
export function generateAlgoliaFiltersFromBrowseFilters(filters: BrowseFilters): string {
  const { characteristics, keyword } = filters
  if (keyword || !characteristics) {
    return ''
  }
  return [CHARACTERISTIC_ID_BLV, CHARACTERISTIC_ID_DHH]
    .filter((characteristic) => !characteristics.includes(characteristic))
    .map((characteristic) => `NOT characteristics:${characteristic}`)
    .join(' AND ')
}

export function facetValuesWithResultsFromAlgoliaFacetResponse(facets: Record<string, Record<string, number>> = {}): FacetValuesWithResults {
  const valuesOf = (facet: string) => (facets[facet] ? Object.keys(facets[facet]) : null)
  return {
    grades: valuesOf(FACET_GRADES),
    domains: valuesOf(FACET_DOMAINS),
    standards: valuesOf(FACET_STANDARDS)
  }
}
